"use client";

import { useReducedMotion } from "motion/react";
import { type SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AUDIO_METADATA_READY_STATE } from "./constants";

export type DubbedVideoPlayerOptions = {
  initialTrackKey: string;
  onBeforePlay?: () => void;
  preloadTrackKeys?: Iterable<string>;
};

export function useDubbedVideoPlayer({ initialTrackKey, onBeforePlay, preloadTrackKeys }: DubbedVideoPlayerOptions) {
  const [activeTrackKey, setActiveTrackKey] = useState(initialTrackKey);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progressAnimationStartedAt, setProgressAnimationStartedAt] = useState<number | null>(null);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const reducedMotion = useReducedMotion() ?? false;
  const activeTrackKeyRef = useRef(initialTrackKey);
  const audioElements = useRef(new Map<string, HTMLAudioElement>());
  const progressAnimationFrameRef = useRef<number | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const warmedAudioTrackKeysRef = useRef(new Set<string>());
  const preloadTrackKeySet = useMemo(() => new Set(preloadTrackKeys ?? []), [preloadTrackKeys]);

  const pauseAudioTracks = useCallback((exceptTrackKey?: string) => {
    audioElements.current.forEach((audio, trackKey) => {
      if (trackKey === exceptTrackKey) return;

      audio.pause();
    });
  }, []);

  const stopVideoProgressTracking = useCallback(() => {
    if (progressAnimationFrameRef.current === null) return;

    window.cancelAnimationFrame(progressAnimationFrameRef.current);
    progressAnimationFrameRef.current = null;
  }, []);

  const syncVideoProgress = useCallback((element: HTMLVideoElement) => {
    setVideoCurrentTime(element.currentTime);
    setVideoDuration(Number.isFinite(element.duration) ? element.duration : 0);
  }, []);

  const warmAudioTrack = useCallback(
    (trackKey: string) => {
      if (preloadTrackKeySet.has(trackKey)) return;
      if (warmedAudioTrackKeysRef.current.has(trackKey)) return;

      const audioElement = audioElements.current.get(trackKey);
      if (!audioElement) return;

      audioElement.preload = "auto";
      audioElement.load();
      warmedAudioTrackKeysRef.current.add(trackKey);
    },
    [preloadTrackKeySet],
  );

  const playAudioTrack = useCallback(
    (trackKey: string, syncedVideoElement: HTMLVideoElement) => {
      const audioElement = audioElements.current.get(trackKey);
      pauseAudioTracks(trackKey);
      if (!audioElement) return;

      syncAudioToVideo(audioElement, syncedVideoElement);
      audioElement.play().catch(() => {});
    },
    [pauseAudioTracks],
  );

  const playMediaTrack = useCallback(
    (trackKey: string) => {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      onBeforePlay?.();
      warmAudioTrack(trackKey);

      if (videoElement.ended) {
        videoElement.currentTime = 0;
      }

      if (!videoElement.paused) {
        playAudioTrack(trackKey, videoElement);
      }

      videoElement.play().catch(() => {});
    },
    [onBeforePlay, playAudioTrack, warmAudioTrack],
  );

  const selectTrack = useCallback(
    (trackKey: string) => {
      const wasTrackActive = trackKey === activeTrackKeyRef.current;

      activeTrackKeyRef.current = trackKey;

      if (!wasTrackActive) {
        setActiveTrackKey(trackKey);
      }

      playMediaTrack(trackKey);
    },
    [playMediaTrack],
  );

  const pausePlayback = useCallback(() => {
    pauseAudioTracks();
    videoRef.current?.pause();
  }, [pauseAudioTracks]);

  const togglePlayback = useCallback(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    if (videoElement.paused) {
      playMediaTrack(activeTrackKeyRef.current);
      return;
    }

    pausePlayback();
  }, [pausePlayback, playMediaTrack]);

  const startVideoProgressTracking = useCallback(
    (element: HTMLVideoElement) => {
      stopVideoProgressTracking();
      syncVideoProgress(element);

      const updateProgress = () => {
        syncVideoProgress(element);

        if (element.paused) {
          progressAnimationFrameRef.current = null;
          return;
        }

        progressAnimationFrameRef.current = window.requestAnimationFrame(updateProgress);
      };

      progressAnimationFrameRef.current = window.requestAnimationFrame(updateProgress);
    },
    [stopVideoProgressTracking, syncVideoProgress],
  );

  const handleVideoPlay = useCallback(
    (event: SyntheticEvent<HTMLVideoElement>) => {
      setIsPlaying(true);
      setProgressAnimationStartedAt(startedAt => startedAt ?? performance.now());
      playAudioTrack(activeTrackKeyRef.current, event.currentTarget);
      startVideoProgressTracking(event.currentTarget);
    },
    [playAudioTrack, startVideoProgressTracking],
  );

  const handleVideoPause = useCallback(
    (event: SyntheticEvent<HTMLVideoElement>) => {
      setIsPlaying(false);
      pauseAudioTracks();
      stopVideoProgressTracking();
      syncVideoProgress(event.currentTarget);
    },
    [pauseAudioTracks, stopVideoProgressTracking, syncVideoProgress],
  );

  const handleVideoEnded = useCallback(() => {
    setIsPlaying(false);
    audioElements.current.forEach(audio => {
      audio.pause();
      setAudioCurrentTime(audio, 0);
    });
    stopVideoProgressTracking();
    setVideoCurrentTime(0);
    setProgressAnimationStartedAt(null);
  }, [stopVideoProgressTracking]);

  const handleVideoSeeked = useCallback(
    (event: SyntheticEvent<HTMLVideoElement>) => {
      const audioElement = audioElements.current.get(activeTrackKeyRef.current);

      syncVideoProgress(event.currentTarget);
      if (audioElement) syncAudioToVideo(audioElement, event.currentTarget);
    },
    [syncVideoProgress],
  );

  const handleVideoProgressChange = useCallback(
    (event: SyntheticEvent<HTMLVideoElement>) => {
      syncVideoProgress(event.currentTarget);
    },
    [syncVideoProgress],
  );

  const handleVideoLoadedData = useCallback(
    (event: SyntheticEvent<HTMLVideoElement>) => {
      syncVideoProgress(event.currentTarget);
    },
    [syncVideoProgress],
  );

  const registerAudioElement = useCallback((trackKey: string, audioElement: HTMLAudioElement | null) => {
    if (audioElement) {
      audioElements.current.set(trackKey, audioElement);
      return;
    }

    audioElements.current.delete(trackKey);
  }, []);

  useEffect(() => {
    return () => {
      pauseAudioTracks();
      stopVideoProgressTracking();
    };
  }, [pauseAudioTracks, stopVideoProgressTracking]);

  return {
    activeTrackKey,
    isPlaying,
    pausePlayback,
    playMediaTrack,
    progressAnimationStartedAt,
    reducedMotion,
    registerAudioElement,
    selectTrack,
    togglePlayback,
    videoCurrentTime,
    videoDuration,
    videoEventHandlers: {
      onDurationChange: handleVideoProgressChange,
      onEnded: handleVideoEnded,
      onLoadedData: handleVideoLoadedData,
      onLoadedMetadata: handleVideoProgressChange,
      onPause: handleVideoPause,
      onPlay: handleVideoPlay,
      onSeeked: handleVideoSeeked,
    },
    videoRef,
    warmAudioTrack,
  };
}

function syncAudioToVideo(audioElement: HTMLAudioElement, videoElement: HTMLVideoElement) {
  const videoCurrentTime = videoElement.ended ? 0 : videoElement.currentTime;

  if (audioElement.readyState >= AUDIO_METADATA_READY_STATE) {
    setAudioCurrentTime(audioElement, videoCurrentTime);
    return;
  }

  audioElement.addEventListener("loadedmetadata", () => setAudioCurrentTime(audioElement, videoCurrentTime), {
    once: true,
  });
  audioElement.load();
}

function setAudioCurrentTime(audioElement: HTMLAudioElement, currentTime: number) {
  if (audioElement.readyState < AUDIO_METADATA_READY_STATE) return;

  const duration = Number.isFinite(audioElement.duration) ? audioElement.duration : currentTime;
  audioElement.currentTime = Math.min(currentTime, duration);
}
